/**
 * The brochure's pages and image slots, plus the carousel one slot's images are
 * picked from. Shared by the wizard's page list, `wizardUtils` and the tests so
 * the page ids and slot keys the renderer expects live in one place.
 */

import ThumbnailCarousel from "../../ui/ThumbnailCarousel";

/**
 * The brochure's image-bearing pages, in page order, each grouping the image
 * slot(s) that compose it. A page's `id` matches the renderer's page id, and is
 * what saved drafts and queued jobs carry.
 */
export const BROCHURE_PAGES = [
  {
    id: "cover",
    label: "Front Cover",
    slots: [{ key: "cover", label: "Front cover (square)" }],
    // This is the legacy per-instance cover used outside the wizard. The
    // wizard's report-level cover has its own default-on switch.
    fixed: true,
  },
  // The ids stay as they are while the names on screen change.
  {
    id: "details",
    label: "Artwork Hero",
    // The shape isn't imposed here: the page anchors its image by whichever of
    // the three shapes it is set as, chosen on the page's own row.
    slots: [{ key: "details", label: "Artwork Hero image" }],
  },
  {
    id: "landscapes",
    label: "Two Details",
    slots: [
      { key: "landscapeTop", label: "Top (landscape)" },
      { key: "landscapeBottom", label: "Bottom (landscape)" },
    ],
  },
  {
    id: "fullBleed",
    label: "Full Bleed",
    slots: [{ key: "fullBleed", label: "Full-bleed (portrait)" }],
  },
  {
    id: "biography",
    label: "Biography",
    description: "One page per artist, with their biography.",
    slots: [],
  },
  {
    id: "publicCollections",
    label: "Selected Public Collections",
    description:
      "One page per artist listing their selected public collections. The " +
      "list is suggested from the artist's events and can be edited below.",
    slots: [],
    // Off by default — it only prints when explicitly toggled on.
    defaultOff: true,
  },
];

/**
 * Flat list of every image slot, in page order. Derived from BROCHURE_PAGES so
 * the slot keys live in a single place.
 */
export const BROCHURE_SLOTS = BROCHURE_PAGES.flatMap(page => page.slots);

const DEFAULT_OFF_PAGE_IDS = new Set(
  BROCHURE_PAGES.filter(page => page.defaultOff).map(page => page.id),
);

/**
 * Pages that stop defaulting on once the document covers several works. An
 * artist biography suits a single-work presentation; across a twelve-work
 * cohort it repeats for every artist and reads as padding, so there it has to
 * be asked for. Mirrors the renderer's own multi-instance rule.
 */
export const MULTI_INSTANCE_DEFAULT_OFF_PAGE_IDS = ["biography"];

/**
 * Pure: does a page need an explicit `true` to print? Either because it is
 * always off by default, or because the document covers several works.
 */
export const isBrochurePageDefaultOff = (
  pageId,
  { multiInstance = false } = {},
) =>
  DEFAULT_OFF_PAGE_IDS.has(pageId) ||
  (multiInstance && MULTI_INSTANCE_DEFAULT_OFF_PAGE_IDS.includes(pageId));

const FIXED_PAGE_IDS = new Set(
  BROCHURE_PAGES.filter(page => page.fixed).map(page => page.id),
);

/**
 * Pure: is this page part of every brochure, whatever the toggles say? The front
 * cover is: it opens the document and can't be dropped, so a draft saved while
 * it was still optional keeps it too.
 */
export const isBrochurePageFixed = pageId => FIXED_PAGE_IDS.has(pageId);

/**
 * Pure: is a page included, given the user's explicit toggles? Most pages
 * default on (only an explicit `false` drops them); default-off pages need an
 * explicit `true` to print. Mirrors the renderer.
 */
export const isBrochurePageEnabled = (enabledPages, pageId, options) =>
  isBrochurePageFixed(pageId) ||
  (enabledPages?.[pageId] ?? !isBrochurePageDefaultOff(pageId, options));

/**
 * Pure: flip one page's toggle, returning the next `enabledPages` map.
 * Default-aware, so the first click on a default-off page turns it on. A fixed
 * page has no toggle to flip.
 */
export const toggleBrochurePage = (enabledPages = {}, pageId, options) =>
  isBrochurePageFixed(pageId)
    ? enabledPages
    : {
        ...enabledPages,
        [pageId]: !isBrochurePageEnabled(enabledPages, pageId, options),
      };

// Thumbnail dimensions per detected image orientation so each thumbnail is
// shown in its own (portrait / landscape / square) aspect.
const ORIENTATION_THUMBNAIL_CLASS = {
  portrait: "h-24 w-16",
  landscape: "h-16 w-24",
  square: "h-16 w-16",
};

/**
 * A single brochure slot: its label plus the artwork's images to pick from, a
 * page of them at a time. Choosing one elsewhere — the wizard fills an empty
 * slot from the primary image — turns the carousel to the page it is on.
 */
export const SlotCarousel = ({
  slot,
  images,
  selectedArchiveId,
  orientations,
  disabled,
  onSelect,
  onImageLoad,
}) => (
  <div className="min-w-0">
    <div className="text-xs font-medium text-gray-700">{slot.label}</div>
    <ThumbnailCarousel
      className="mt-1"
      label={`${slot.label} images`}
      selectedIndex={images.findIndex(
        image => image.archiveId === selectedArchiveId,
      )}
    >
      {images.map(image => {
        const selected = selectedArchiveId === image.archiveId;
        const thumbnailClass =
          ORIENTATION_THUMBNAIL_CLASS[orientations[image.archiveId]] ||
          ORIENTATION_THUMBNAIL_CLASS.square;
        return (
          <button
            key={`${slot.key}-${image.archiveId}`}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(image.archiveId)}
            className={`${thumbnailClass} relative overflow-hidden border-2 transition ${
              selected
                ? "border-blue-600 opacity-100 ring-2 ring-blue-400 ring-offset-2"
                : "border-transparent opacity-60 hover:opacity-100"
            }`}
            title={`Archive ${image.archiveId}`}
          >
            <img
              src={image.imgT}
              alt={`Artwork image ${image.archiveId}`}
              onLoad={onImageLoad(image.archiveId)}
              className="h-full w-full object-cover"
            />
          </button>
        );
      })}
    </ThumbnailCarousel>
  </div>
);
